'use client';

import React, { useCallback } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { Picture } from 'app/components/Picture';
import { type UseCarouselReturnType } from './useCarousel';
import { CarouselNextButton } from './CarouselNextButton';
import { CarouselDotButton } from './CarouselDotButton';

type CarouselViewportPropType = {
    isBuyer?: boolean;
    isEditingMode?: boolean;
    viewportRef: UseCarouselReturnType['state']['emblaMainRef'];
    api: {
        main: UseCarouselReturnType['action']['emblaMainApi'];
    };
    slides: UseCarouselReturnType['state']['slides'];
    selectedIndex: UseCarouselReturnType['state']['selectedIndex'];
    onDotButtonClick: UseCarouselReturnType['action']['onDotButtonClick'];
};

export const CarouselViewport: React.FC<CarouselViewportPropType> = (props) => {
    const { api, viewportRef, slides, selectedIndex, onDotButtonClick } = props;

    const LinkComponent = useCallback(
        (_props: {
            slide: UseCarouselReturnType['state']['slides'][number];
            children: React.ReactNode;
        }) => {
            const { slide, children } = _props;

            if (slide.link) {
                return (
                    <Link
                        href={slide?.link}
                        target={slide?.shouldOpenNewWindow ? '_blank' : '_self'}
                    >
                        {children}
                    </Link>
                );
            }

            return children;
        },
        []
    );

    const onPrevButtonClick = useCallback(() => {
        if (!api.main) {
            return;
        }

        api.main.scrollPrev();
    }, [api]);

    const onNextButtonClick = useCallback(() => {
        if (!api.main) {
            return;
        }

        api.main.scrollNext();
    }, [api]);

    return (
        <div className="carousel__viewport-box">
            <div className="carousel__viewport" ref={viewportRef}>
                {api.main?.canScrollPrev() && (
                    <CarouselNextButton onClick={onPrevButtonClick} isPrevious />
                )}
                <div
                    className={clsx(
                        'carousel__container',
                        slides.length === 1 && 'carousel__container--single'
                    )}
                >
                    {slides.map((slide, idx) => (
                        <div className="carousel__slide" key={slide.id + idx}>
                            <div className="carousel__slide__content">
                                <div className="carousel__slide__content__inner">
                                    <LinkComponent slide={slide}>
                                        <Picture
                                            sources={slide?.sources}
                                            alt="Carousel Image"
                                            draggable={false}
                                        />
                                    </LinkComponent>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {api.main?.canScrollNext() && (
                    <CarouselNextButton onClick={onNextButtonClick} />
                )}
            </div>
            <div className="carousel__dot-button-container">
                {Array.from({ length: slides.length }).map((_, idx) => (
                    <CarouselDotButton
                        key={idx}
                        onClick={() => onDotButtonClick(idx)}
                        isActive={selectedIndex === idx}
                    />
                ))}
            </div>
        </div>
    );
};
